/**
 * Solid bindings for the roving-focus core.
 *
 *   const nav = createRovingFocus({ orientation: "both", vimKeys: true });
 *   <button ref={nav.item("list-icon-btn")} … />
 *   onKeyDown={(e) => nav.handleKeyDown(e)}
 *
 * `activeId()` is a reactive accessor (handy for `aria-activedescendant`).
 */

import { type Accessor, createSignal, onCleanup } from "solid-js";

import { createRovingCore } from "./roving";
import type { RovingConfig, RovingCore } from "./types";

interface RovingFocus extends Omit<RovingCore, "register" | "unregister"> {
  /** Ref factory: `ref={roving.item("id")}` registers/unregisters the element. */
  item: (id: string) => (el: HTMLElement) => void;
  /** Reactive active item id. */
  activeId: Accessor<string | null>;
}

export function createRovingFocus(config: RovingConfig = {}): RovingFocus {
  const [activeId, setActiveId] = createSignal<string | null>(null);

  const core = createRovingCore({
    ...config,
    onActiveChange: (id) => {
      setActiveId(id);
      config.onActiveChange?.(id);
    },
  });

  const item = (id: string) => (el: HTMLElement) => {
    core.register(id, el);
    onCleanup(() => {
      core.unregister(id);
    });
  };

  return {
    item,
    activeId,
    items: core.items,
    getActive: core.getActive,
    setActive: core.setActive,
    move: core.move,
    first: core.first,
    last: core.last,
    handleKeyDown: core.handleKeyDown,
  };
}
